import type { ProcessedSet } from "@workspace/types";
import { useMemo } from "react";

interface Stage {
	id: string;
	name: string;
	position?: number;
}

/**
 * Hook to filter stages based on which ones have sets for the selected day
 * Also handles resetting stage selection when it becomes invalid
 */
export function useFilteredStages({
	dbStagesList,
	processedSets,
	selectedDay,
	selectedStage,
	handleStageChange,
}: {
	dbStagesList: Stage[];
	processedSets: ProcessedSet[];
	selectedDay: string;
	selectedStage: string;
	handleStageChange: (stageId: string) => void;
}) {
	// Filter stages to only include those with sets for the selected day
	const filteredStages = useMemo(() => {
		return dbStagesList.filter((stage) => {
			// Check if this stage has any sets for the selected day
			return processedSets.some(
				(set) =>
					set.stage?.id === stage.id &&
					(selectedDay === "all" || set.day === selectedDay),
			);
		});
	}, [dbStagesList, processedSets, selectedDay]);

	// Reset stage selection if the currently selected stage doesn't have sets for the new day
	useMemo(() => {
		if (selectedStage !== "all") {
			const stageStillValid = filteredStages.some(
				(stage) => stage.id === selectedStage,
			);
			if (!stageStillValid) {
				handleStageChange("all");
			}
		}
	}, [filteredStages, selectedStage, handleStageChange]);

	return filteredStages;
}
